// Two-phase UI, mobile-first:
//  · Search phase — one search box; picking a result fills the box + dismisses keyboard.
//  · Routing phase — From input + 2 tri-state toggles while calculating.
//  · Route found — compact pill at the BOTTOM with time/level + ⌃ share/save menu.
//  · Nav confirmed — same pill, no GO button.

import { useMemo, useRef, useState } from 'react';
import { searchRooms, roomLabel, roomNumberBadge } from '../lib/graph.js';
import { courseLabel, courseRoomEntry, courseRows } from '../lib/courses.js';
import QrPopup from './QrPopup.jsx';

export default function SearchPanel({
  roomIndex, buildings,
  routing,
  selectedRoom,
  onPickRoom, onClearRoom,
  onGo, onBack, onFindRoute,
  fromText, startPin, onFromTextChange, onFromPick, onClearFrom,
  accessibleMode, onAccessibleMode,
  tunnelMode, onTunnelMode,
  status, route, activeLeg,
  routeConfirmed, onConfirmRoute, onUnconfirm,
  walkSpeed, shareUrl, onOpenSupport, onOpenReport,
  // The host draws the QR in the ⌃ menu's popup — the mark in the middle of it
  // is a host asset, and nothing branded lives inside this folder. See QrPopup.
  renderQr,
  courseMode, onCourseMode, courseIndex, courseStatus,
  // The host measures this card to place whatever hangs below it (the Events
  // button). Only the search view carries it: the directions card and the route
  // pill own the screen themselves, and nothing hangs off them.
  cardRef,
}) {
  if (!routing) {
    return (
      <div className="pfa-panel pfa-panel-search" ref={cardRef}>
        <SearchView
          roomIndex={roomIndex}
          selectedRoom={selectedRoom}
          onPickRoom={onPickRoom}
          onClearRoom={onClearRoom}
          onGo={onGo}
          fromText={fromText}
          startPin={startPin}
          onClearFrom={onClearFrom}
          onOpenSupport={onOpenSupport}
          courseMode={courseMode}
          onCourseMode={onCourseMode}
          courseIndex={courseIndex}
          courseStatus={courseStatus}
        />
      </div>
    );
  }

  const mins = route ? Math.max(1, Math.round(route.totalMeters / (walkSpeed || 80))) : null;
  const meters = route ? Math.round(route.totalMeters) : null;

  const toggleProps = { accessibleMode, onAccessibleMode, tunnelMode, onTunnelMode };

  // Route found → nav pill at bottom. There is no separate "preview, then tap
  // GO" pill any more: finding the route IS the request to go, and the whole-
  // route overview plays automatically before the camera flies to the start.
  // The pill is identical either side of that beat, so it does not flicker
  // while the camera moves.
  if (route) {
    return (
      <RoutePill onBack={onUnconfirm} shareUrl={shareUrl} toggleProps={toggleProps} renderQr={renderQr}>
        <div className="pfa-pill-info">
          <div className="pfa-pill-time">~{mins} min · {meters} m</div>
          <LevelBadge route={route} activeLeg={activeLeg} buildings={buildings} />
        </div>
      </RoutePill>
    );
  }

  // Routing but no route yet → collapsible Directions sheet (bottom)
  return (
    <div className="pfa-panel pfa-panel-directions">
      <RoutingInputView
        roomIndex={roomIndex}
        fromText={fromText}
        startPin={startPin}
        onFromTextChange={onFromTextChange}
        onFromPick={onFromPick}
        onBack={onBack}
        onFindRoute={onFindRoute}
        accessibleMode={accessibleMode}
        onAccessibleMode={onAccessibleMode}
        tunnelMode={tunnelMode}
        onTunnelMode={onTunnelMode}
        status={status}
        onOpenReport={onOpenReport}
      />
    </div>
  );
}

// ── Pill wrapper (bottom sheet) ──────────────────────────────────────────────
function RoutePill({ onBack, shareUrl, toggleProps, renderQr, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null); // 'copied'
  const [qrOpen, setQrOpen] = useState(false);

  const showToast = msg => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const handleShare = async () => {
    // Copy ONLY the deep link to this exact route — no share sheet, no extra text.
    const url = shareUrl || window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for browsers without the async clipboard API.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch { /* ignore — still show confirmation */ }
    showToast('copied');
    setMenuOpen(false);
  };

  const openQr = () => {
    setQrOpen(true);
    setMenuOpen(false);
  };

  return (
    <div className={`pfa-panel pfa-panel-pill${menuOpen ? ' menu-open' : ''}`}>
      {/* Backdrop to close the menu when tapping outside */}
      {menuOpen && (
        <div className="pfa-pill-backdrop" onClick={() => setMenuOpen(false)} />
      )}

      {/* Toast notification */}
      {toast && (
        <div className="pfa-toast">✓ Copied!</div>
      )}

      {qrOpen && <QrPopup url={shareUrl} renderQr={renderQr} onClose={() => setQrOpen(false)} />}

      {/* ⌃ dropdown menu */}
      {menuOpen && (
        <div className="pfa-pill-menu">
          {toggleProps && <OptionToggles {...toggleProps} />}
          <button className="pfa-pill-menu-btn" onClick={handleShare}>
            <span className="pfa-pill-menu-icon">🔗</span> Copy Route Link
          </button>
          <button className="pfa-pill-menu-btn" onClick={openQr}>
            <span className="pfa-pill-menu-icon">🔳</span> QR Code
          </button>
        </div>
      )}

      <button className="pfa-back" onClick={onBack} aria-label="Back">←</button>
      {children}
      <button
        className={`pfa-pill-more ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(m => !m)}
        aria-label="More options"
      >⌃</button>
    </div>
  );
}

// ── Search phase ─────────────────────────────────────────────────────────────
function SearchView({
  roomIndex, selectedRoom, onPickRoom, onClearRoom, onGo, fromText, startPin, onClearFrom,
  onOpenSupport, courseMode, onCourseMode, courseIndex, courseStatus,
}) {
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  // The course row behind the current selection, so the panel can say WHICH
  // class this room is — "Drake Centre 122" alone does not confirm you picked
  // the right section, and the section is what was typed.
  const [pickedCourse, setPickedCourse] = useState(null);
  const inputRef = useRef(null);
  const results = useMemo(
    () => (q.trim() && !selectedRoom && !courseMode ? searchRooms(roomIndex, q) : []),
    [roomIndex, q, selectedRoom, courseMode]
  );
  const courses = useMemo(
    () => (q.trim() && !selectedRoom && courseMode ? courseRows(courseIndex, q) : []),
    [courseIndex, q, selectedRoom, courseMode]
  );
  // What the chip says the start is. A scanned sign names the PLACE — the
  // building it is in — because that is an address the reader can recognise;
  // its printed identifier goes underneath in small type, where it does the one
  // job it is good for: checking this is the sign in front of you.
  const fromChip = (fromText || '').trim().replace(/^📍\s*/, '') || startPin?.place || '';
  const fromCode = !((fromText || '').trim()) && startPin?.code ? startPin.code : '';

  const handlePick = r => {
    onPickRoom(r);
    setPickedCourse(null);
    setQ(roomLabel(r));
    setFocus(false);
    inputRef.current?.blur();
  };

  // A course is picked by its section; what gets selected is its ROOM. From
  // here on nothing downstream knows a course was involved — the pin, Find
  // Route and the shared link are the room's, which is the point of the mode.
  const handlePickCourse = row => {
    const entry = courseRoomEntry(roomIndex, row);
    if (!entry) return; // the row is already disabled; belt and braces
    // `via` is the one thing the room entry cannot carry: two people can walk to
    // Armes 200, and only one of them got there by typing a course code. It is
    // what the usage log counts to answer whether this feature is used at all.
    onPickRoom(entry, { via: 'course' });
    setPickedCourse(row);
    setQ(courseLabel(row));
    setFocus(false);
    inputRef.current?.blur();
  };

  const handleChange = e => {
    const val = e.target.value;
    setQ(val);
    setFocus(true);
    if (!val && selectedRoom) { onClearRoom(); setPickedCourse(null); }
  };

  // Switching modes empties the box. The two searches take different strings —
  // "MATH 1500 A02" finds no room and "Armes 200" no course — so carrying the
  // old text across would show an empty list that reads as the new mode being
  // broken.
  const toggleCourseMode = () => {
    const next = !courseMode;
    setQ('');
    setPickedCourse(null);
    if (selectedRoom) onClearRoom();
    onCourseMode?.(next);
    setFocus(true);
    inputRef.current?.focus();
  };

  return (
    <>
      {/* One button in this corner, not two. The "!" report button used to sit
          beside the "?", and the split never held: a wrong room and a thank-you
          are both "something I want to tell you", and nobody sorts themselves
          into the right one of two round buttons. The "?" now carries the
          address for all of it. The in-context "Report a problem" link under a
          FAILED route stays — that one appears at the moment there is something
          specific to report, which is different from a button that is always
          there. */}
      <button
        className="pfa-search-support"
        onClick={onOpenSupport}
        aria-label="About UMaps"
      >?</button>
      <div className="pfa-search-head">
        <div className="pfa-search-title-row">
          <div className="pfa-brand">{courseMode ? 'Find a class' : 'Find a room'}</div>
          {/* The one switch on this screen that changes what the box searches,
              so it sits on the title — the line that says what the box is
              for — rather than among the route options, which are about a walk
              that has not been asked for yet. */}
          <button
            type="button"
            className={`pfa-mode-toggle${courseMode ? ' on' : ''}`}
            onClick={toggleCourseMode}
            aria-pressed={!!courseMode}
            title={courseMode ? 'Search rooms instead' : 'Search your timetable by course code'}
          >Course Mode</button>
        </div>
        {fromChip && (
          <div className="pfa-from-chip" title={`Starting from ${fromChip}`}>
            <span className="pfa-from-chip-text">
              From {fromChip}
              {fromCode && <span className="pfa-from-chip-code">sign {fromCode}</span>}
            </span>
            <button
              className="pfa-from-chip-x"
              onClick={onClearFrom}
              aria-label="Clear starting location"
            >×</button>
          </div>
        )}
      </div>
      <div className="pfa-field">
        <input
          ref={inputRef}
          className="pfa-input pfa-input-lg"
          type="text"
          placeholder={courseMode
            ? 'Search your course — e.g. MATH 1500 A02'
            : 'Search for a room — e.g. Biological Sciences 100'}
          value={q}
          onChange={handleChange}
          onFocus={() => { setFocus(true); if (selectedRoom) { setQ(''); onClearRoom(); setPickedCourse(null); } }}
          onClick={() => setFocus(true)}
          onBlur={() => setTimeout(() => setFocus(false), 150)}
        />
        {focus && !courseMode && results.length > 0 && (
          <div className="pfa-results">
            {results.map(r => (
              <ResultRow key={`${r.building}-${r.nodeId}`} entry={r} onPick={() => handlePick(r)} />
            ))}
          </div>
        )}
        {focus && courseMode && (
          <CourseResults
            rows={courses}
            roomIndex={roomIndex}
            query={q}
            status={courseStatus}
            termName={courseIndex?.termName}
            onPick={handlePickCourse}
          />
        )}
      </div>

      {/* Which class this room is. The box now reads "MATH 1500 A02", which is
          what was typed, not where it is — so the answer goes underneath. */}
      {selectedRoom && pickedCourse && (
        <div className="pfa-course-picked">
          <span className="pfa-course-picked-room">{roomLabel(selectedRoom)}</span>
          {pickedCourse.when && <span className="pfa-course-picked-when">{pickedCourse.when}</span>}
        </div>
      )}

      {selectedRoom && (
        <button className="pfa-find-route" onClick={onGo}>Find Route →</button>
      )}
    </>
  );
}

// ── One autocomplete result ──────────────────────────────────────────────────
// Shared by the destination box and the From box, which offer the same index
// and had drifted into two copies of the same markup.
//
// A BUS STOP says so. Its label is a street rather than a room ("Westbound
// Dafoe at U of M Station"), so among a list of rooms it reads as one until you
// have already tapped it — and it behaves differently once you do: a stop is
// outdoors, on nobody's floor, and what you get there is a timetable. The pale
// blue and the tag are that warning, and they double as the answer to "did my
// 'bus' search find anything", which is the query these are ranked last for.
/**
 * The three things in this list that are not rooms — a bus stop, a parking
 * area, a bike rack — each with its own colour and word.
 *
 * They need saying because their names do not: "Q Lot – Student Permit" reads
 * like a room code, "Science Complex Bike Parking" reads like somewhere you
 * would drive to, and a stop's name is a street. The colour is the same one the
 * thing is drawn in on the map, so a row and what it takes you to are
 * recognisably one object. A dropoff bay is separated from the lots on purpose:
 * you cannot park in one, and "PARKING" on it would be a small lie repeated
 * seven times.
 */
const RESULT_KINDS = {
  bus: { cls: 'bus', tag: 'BUS STOP' },
  lot: { cls: 'parking', tag: 'PARKING' },
  dropoff: { cls: 'dropoff', tag: 'DROPOFF' },
  bike: { cls: 'bike', tag: 'BIKE' },
};

function resultKind(entry) {
  if (entry.busStop) return RESULT_KINDS.bus;
  return RESULT_KINDS[entry.parkingKind] || null;
}

function ResultRow({ entry, onPick }) {
  const unreachable = entry.connected === false;
  const badge = roomNumberBadge(entry);
  const kind = resultKind(entry);
  return (
    <button
      className={`pfa-result${kind ? ` pfa-result-${kind.cls}` : ''}${unreachable ? ' pfa-result-disabled' : ''}`}
      disabled={unreachable}
      onMouseDown={e => e.preventDefault()}
      onClick={onPick}
    >
      <span className="pfa-result-room">
        {roomLabel(entry)}
        {/* Which way this one goes, in the unit somebody catching a bus thinks
            in. Two stops face each other across Dafoe with nearly the same
            name, and in a list of results "Westbound" versus "Eastbound" is the
            only thing between them — a distinction that needs you to know which
            way the campus lies from the road you are standing on. */}
        {entry.busNote && <span className="pfa-result-note">({entry.busNote})</span>}
      </span>
      {/* "no route" outranks the tag: what a row cannot do matters more than
          what kind of thing it is. */}
      {unreachable
        ? <span className="pfa-result-bldg">no route</span>
        : kind
          ? (
            <span className={`pfa-result-tag ${kind.cls}`}>
              {kind.tag}
              {/* Costs money. The one fact about a lot that changes whether
                  somebody drives to it at all, and the row already had the
                  space — the alternative was reading four "Q Lot – …" rows and
                  tapping each to find out which one is free. Only areas that
                  actually charge: an accessible bay's rate is a requirement
                  rather than a tariff, so `lotRate` returns nothing for it and
                  no dollar is drawn (see lib/parking.js). */}
              {entry.paid && (
                <span className="pfa-result-paid" title={entry.rate || 'Paid parking'}>$</span>
              )}
            </span>
          )
          : badge && <span className="pfa-result-bldg">{badge}</span>}
    </button>
  );
}

// ── Course Mode results ──────────────────────────────────────────────────────
// Unlike the room list, this one is rendered even when it is EMPTY. Everything
// that can go wrong here is invisible otherwise: the timetable is fetched on
// the first toggle, so an early keystroke arrives before it does; a snapshot
// without a courses.json answers null; and a term simply may not have the
// section somebody typed. All three look identical to "the box is broken"
// unless the list says which it is.
function CourseResults({ rows, roomIndex, query, status, termName, onPick }) {
  if (status === 'loading') {
    return <div className="pfa-results"><div className="pfa-course-msg">Loading the timetable…</div></div>;
  }
  if (status === 'unavailable') {
    return <div className="pfa-results"><div className="pfa-course-msg">Course times aren’t available right now.</div></div>;
  }
  if (!query.trim()) {
    return (
      <div className="pfa-results">
        <div className="pfa-course-msg">
          Type a course code{termName ? ` — ${termName} classes` : ''}.
        </div>
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="pfa-results"><div className="pfa-course-msg">No {termName || 'timetabled'} class matches “{query.trim()}”.</div></div>;
  }
  return (
    <div className="pfa-results">
      {rows.map(row => (
        <CourseRow key={row.key} row={row} entry={courseRoomEntry(roomIndex, row)} onPick={() => onPick(row)} />
      ))}
    </div>
  );
}

/**
 * One section, in one room. Two lines, because a course row has to answer two
 * questions at once: "is this my section" (the code, the section number, the
 * meeting time) and "where is it" (the room) — and a student with two sections
 * of the same course picks on the time.
 *
 * A row with no reachable room is shown and disabled rather than dropped: an
 * online section, or one of the three timetabled rooms with no door node in the
 * snapshot, is a real answer to "where is my class", and hiding it would make
 * the search look like it had never heard of the course.
 */
function CourseRow({ row, entry, onPick }) {
  const unreachable = !entry || entry.connected === false;
  const why = !row.roomId ? 'no room' : !entry ? 'not on map' : 'no route';
  return (
    <button
      className={`pfa-result pfa-result-course${unreachable ? ' pfa-result-disabled' : ''}`}
      disabled={unreachable}
      onMouseDown={e => e.preventDefault()}
      onClick={onPick}
    >
      <span className="pfa-course-main">
        <span className="pfa-result-room">
          {row.code} <span className="pfa-course-section">{row.section}</span>
        </span>
        <span className="pfa-course-sub">{row.when || row.title}</span>
      </span>
      {unreachable
        ? <span className="pfa-result-bldg">{why}</span>
        : <span className="pfa-course-room">{row.roomLabel}</span>}
    </button>
  );
}

// ── Accessible / Tunnel tri-state toggles (shared by the sheet and the pill) ──
function OptionToggles({ accessibleMode, onAccessibleMode, tunnelMode, onTunnelMode }) {
  const cycleAccessible = () => onAccessibleMode((accessibleMode + 1) % 3);
  const cycleTunnel = () => onTunnelMode((tunnelMode + 1) % 3);

  const accessibleLabel = accessibleMode === 2 ? '🪜 Stairs Only' : '♿ Accessible';
  const accessibleCls = accessibleMode === 1 ? 'on-green' : accessibleMode === 2 ? 'on-brown' : '';

  const tunnelLabel = tunnelMode === 2 ? '🚇 No Tunnels' : '🚇 Tunnels';
  const tunnelCls = tunnelMode === 1 ? 'on-green' : tunnelMode === 2 ? 'on-red' : '';

  return (
    <div className="pfa-toggles">
      <button
        className={`pfa-toggle ${accessibleCls}`}
        onClick={cycleAccessible}
        title={
          accessibleMode === 0 ? 'Any route — tap to prefer elevators'
          : accessibleMode === 1 ? 'Elevators only — tap for stairs only'
          : 'Stairs only — tap to reset'
        }
      >
        {accessibleLabel}
      </button>
      <button
        className={`pfa-toggle ${tunnelCls}`}
        onClick={cycleTunnel}
        title={
          tunnelMode === 0 ? 'No tunnel preference — tap to prefer tunnels'
          : tunnelMode === 1 ? 'Prefer tunnels — tap to avoid tunnels'
          : 'Avoid tunnels — tap to reset'
        }
      >
        {tunnelLabel}
      </button>
    </div>
  );
}

// ── Routing input (no route found yet) ───────────────────────────────────────
function RoutingInputView({
  roomIndex, fromText, startPin, onFromTextChange, onFromPick,
  onBack, onFindRoute, accessibleMode, onAccessibleMode,
  tunnelMode, onTunnelMode, status, onOpenReport,
}) {
  const [open, setOpen] = useState(true); // From fields shown on open; chevron can minimize
  // A scanned sign fills this even though the box is empty — the start is real,
  // it just was not typed.
  const fromSummary = fromText.trim().replace(/^📍\s*/, '') || startPin?.place || '';

  return (
    <>
      {/* Body expands upward (rendered above the pinned bar) */}
      {open && (
        <div className="pfa-directions-body">
          <FromField
            roomIndex={roomIndex}
            value={fromText}
            startPin={startPin}
            onTextChange={onFromTextChange}
            onPick={onFromPick}
          />
          <OptionToggles
            accessibleMode={accessibleMode}
            onAccessibleMode={onAccessibleMode}
            tunnelMode={tunnelMode}
            onTunnelMode={onTunnelMode}
          />
          {status.kind === 'error' && (
            <div className="pfa-status pfa-status-error">
              {status.text}
              <button className="pfa-report-link" onClick={onOpenReport}>Report a problem</button>
            </div>
          )}
          <button
            className="pfa-find-route"
            onClick={onFindRoute}
            disabled={!fromText.trim() && !startPin}
          >Find Route →</button>
        </div>
      )}

      {/* Persistent bar — always visible, pinned at the bottom */}
      <div className="pfa-directions-head">
        <button className="pfa-back" onClick={onBack} aria-label="Back to search">←</button>
        <button
          type="button"
          className="pfa-directions-toggle"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          <span className="pfa-directions-titles">
            <span className="pfa-route-title">Directions</span>
            <span className="pfa-directions-sub">
              {fromSummary ? `From ${fromSummary}` : 'Tap to set your starting point'}
            </span>
          </span>
          <span className={`pfa-dir-chevron ${open ? 'open' : ''}`}>⌃</span>
        </button>
      </div>
    </>
  );
}

// ── Level badge ──────────────────────────────────────────────────────────────
function LevelBadge({ route, activeLeg, buildings }) {
  const info = useMemo(() => {
    if (!route) return null;
    const leg = route.legs[activeLeg];
    if (!leg) return null;
    const hasIndoor = leg.nodes.some(n => n.floor && n.floor !== 0);
    if (!hasIndoor) return { outdoor: true };
    let slug = null, floor = null;
    for (const n of leg.nodes) {
      if (n.floor !== 0 && !slug) { slug = n.building; floor = n.floor; }
    }
    for (const n of [...leg.nodes].reverse()) {
      if (n.floor !== 0) { slug = n.building; floor = n.floor; break; }
    }
    if (!floor || floor === 0) return { outdoor: true };
    const bName = buildings.find(b => b.slug === slug)?.name || null;
    return { level: floor * 100, bName, outdoor: false };
  }, [route, activeLeg, buildings]);

  if (!info) return null;
  if (info.outdoor) {
    return <div className="pfa-level-badge">🌤 You should be Outside</div>;
  }
  return (
    <div className="pfa-level-badge">
      📍 Level {info.level}
      {info.bName && <span className="pfa-level-bldg"> · {info.bName}</span>}
    </div>
  );
}

// ── From field ───────────────────────────────────────────────────────────────
// Rooms only — a whole building is never a routable endpoint, so buildings are
// not suggested (the user picks a specific room as their starting point).
function FromField({ roomIndex, value, startPin, onTextChange, onPick }) {
  const [focus, setFocus] = useState(false);

  const items = useMemo(() => {
    const t = value.trim();
    if (!t) return [];
    return searchRooms(roomIndex, t).map(entry => ({ kind: 'room', entry }));
  }, [value, roomIndex]);

  return (
    <div className="pfa-field">
      <label className="pfa-label">From</label>
      {/* Scanned: the start is already the exact node the sign is bolted next
          to, so the box stays empty and says so. Typing still replaces it —
          somebody may have walked on before searching — which is the same rule
          as before, just no longer signalled by a code sitting in the field. */}
      {startPin && !value.trim() && (
        <div className="pfa-from-set">
          <span className="pfa-from-set-place">{startPin.place}</span>
          {startPin.code && <span className="pfa-from-set-code">sign {startPin.code}</span>}
        </div>
      )}
      <input
        className="pfa-input"
        type="text"
        placeholder={startPin && !value.trim()
          ? 'Somewhere else? Type a room number'
          : 'Enter the nearest room number you see — e.g. Armes 200'}
        value={value}
        onFocus={() => setFocus(true)}
        onClick={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        onChange={e => { onTextChange(e.target.value); setFocus(true); }}
      />
      {focus && items.length > 0 && (
        <div className="pfa-results">
          {items.map((it, i) => (
            <ResultRow key={i} entry={it.entry} onPick={() => { onPick(it); setFocus(false); }} />
          ))}
        </div>
      )}
    </div>
  );
}
